<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
    <!-- Success State -->
    <UiCard v-if="success" class="w-full max-w-md">
      <UiCardContent class="px-8 py-12 text-center">
        <div class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-bubba-green/10">
          <Icon name="tabler:mail-check" size="32" class="text-bubba-green" />
        </div>
        <h1 class="text-2xl font-bold tracking-tight mb-2">{{ $t('auth.register.successTitle') }}</h1>
        <p class="text-base text-muted-foreground leading-relaxed mb-8">
          {{ $t('auth.register.successMessage') }}
        </p>
        <NuxtLink :to="$localeRoute('/account/login')">
          <UiButton variant="outline" class="w-full h-11 text-base">
            {{ $t('auth.register.signIn') }}
          </UiButton>
        </NuxtLink>
      </UiCardContent>
    </UiCard>

    <!-- Registration Form -->
    <UiCard v-else class="w-full max-w-md">
      <UiCardHeader class="space-y-1.5 text-center pb-2">
        <h1 class="text-2xl font-bold tracking-tight">{{ $t('auth.register.title') }}</h1>
        <p class="text-base text-muted-foreground">{{ $t('auth.register.description') }}</p>
      </UiCardHeader>
      <UiCardContent class="space-y-5 px-8">
        <!-- Error Alert -->
        <UiAlert v-if="serverError" variant="destructive">
          <Icon name="tabler:alert-circle" size="18" />
          <UiAlertDescription>{{ serverError }}</UiAlertDescription>
        </UiAlert>

        <VeeForm :validation-schema="registerSchema" @submit="handleRegister" class="space-y-5" v-slot="{ meta }">
          <div class="space-y-1.5">
            <UiLabel for="name" class="text-sm font-medium">{{ $t('auth.fields.name') }}</UiLabel>
            <VeeField name="name" v-slot="{ field, errorMessage }">
              <UiInput
                id="name"
                v-bind="field"
                type="text"
                class="h-11"
                :placeholder="$ts('auth.fields.namePlaceholder')"
                autocomplete="name"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <div class="space-y-1.5">
            <UiLabel for="email" class="text-sm font-medium">{{ $t('auth.fields.email') }}</UiLabel>
            <VeeField name="email" v-slot="{ field, errorMessage }">
              <UiInput
                id="email"
                v-bind="field"
                type="email"
                class="h-11"
                :placeholder="$ts('auth.fields.emailPlaceholder')"
                autocomplete="email"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <div class="space-y-1.5">
            <UiLabel for="password" class="text-sm font-medium">{{ $t('auth.fields.password') }}</UiLabel>
            <VeeField name="password" v-slot="{ field, errorMessage }">
              <UiInput
                id="password"
                v-bind="field"
                type="password"
                class="h-11"
                :placeholder="$ts('auth.fields.passwordPlaceholder')"
                autocomplete="new-password"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <div class="space-y-1.5">
            <UiLabel for="confirmPassword" class="text-sm font-medium">{{ $t('auth.fields.confirmPassword') }}</UiLabel>
            <VeeField name="confirmPassword" v-slot="{ field, errorMessage }">
              <UiInput
                id="confirmPassword"
                v-bind="field"
                type="password"
                class="h-11"
                :placeholder="$ts('auth.fields.confirmPasswordPlaceholder')"
                autocomplete="new-password"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>

          <!-- Consent checkboxes -->
          <div class="space-y-3 pt-1">
            <!-- Terms & Privacy — required, unchecked by default -->
            <VeeField name="agreeTerms" v-slot="{ errorMessage: termsError }">
              <label class="flex items-start gap-2.5 cursor-pointer">
                <RekaCheckbox
                  :checked="agreeTerms"
                  class="mt-0.5"
                  @update:checked="(val: boolean) => { agreeTerms = val; }"
                />
                <span class="text-sm leading-snug text-muted-foreground">
                  <i18n-t keypath="auth.register.agreeTerms">
                    <template #terms>
                      <NuxtLink :to="$localeRoute('/legal/terms')" class="font-medium text-foreground hover:underline" target="_blank" rel="noopener noreferrer">{{ $t('auth.register.termsLink') }}</NuxtLink>
                    </template>
                    <template #privacy>
                      <NuxtLink :to="$localeRoute('/legal/privacy')" class="font-medium text-foreground hover:underline" target="_blank" rel="noopener noreferrer">{{ $t('auth.register.privacyLink') }}</NuxtLink>
                    </template>
                  </i18n-t>
                </span>
              </label>
              <p v-if="termsError" class="text-sm text-destructive mt-1 ml-7">{{ termsError }}</p>
            </VeeField>

            <!-- Marketing — optional, unchecked by default -->
            <label class="flex items-start gap-2.5 cursor-pointer">
              <RekaCheckbox
                :checked="agreeMarketing"
                class="mt-0.5"
                @update:checked="(val: boolean) => { agreeMarketing = val; }"
              />
              <span class="text-sm leading-snug text-muted-foreground">{{ $t('auth.register.agreeMarketing') }}</span>
            </label>

            <!-- Analytics — optional, unchecked by default -->
            <label class="flex items-start gap-2.5 cursor-pointer">
              <RekaCheckbox
                :checked="agreeAnalytics"
                class="mt-0.5"
                @update:checked="(val: boolean) => { agreeAnalytics = val; }"
              />
              <span class="text-sm leading-snug text-muted-foreground">{{ $t('auth.register.agreeAnalytics') }}</span>
            </label>
          </div>

          <UiButton type="submit" class="w-full h-11 text-base font-semibold" :disabled="isLoading || !meta.valid || !agreeTerms">
            <Icon v-if="isLoading" name="tabler:loader-2" size="18" class="animate-spin" />
            {{ $t('auth.register.submit') }}
          </UiButton>
        </VeeForm>

        <!-- Divider -->
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <UiSeparator />
          </div>
          <div class="relative flex justify-center text-xs uppercase">
            <span class="bg-card px-2 text-muted-foreground">
              {{ $t('auth.register.orContinueWith') }}
            </span>
          </div>
        </div>

        <!-- Social Login Buttons -->
        <div class="grid grid-cols-2 gap-4">
          <UiButton
            type="button"
            variant="outline"
            class="h-11"
            @click="handleGoogleSignup"
            :disabled="isLoading"
          >
            <Icon name="devicon:google" size="18" />
            Google
          </UiButton>
          <UiButton
            type="button"
            variant="outline"
            class="h-11"
            @click="handleAppleSignup"
            :disabled="isLoading"
          >
            <Icon name="devicon:apple" size="18" />
            Apple
          </UiButton>
        </div>
      </UiCardContent>
      <UiCardFooter class="flex justify-center pb-8">
        <p class="text-sm text-muted-foreground">
          {{ $t('auth.register.hasAccount') }}
          <NuxtLink :to="$localeRoute('/account/login')" class="font-medium text-foreground hover:underline">
            {{ $t('auth.register.signIn') }}
          </NuxtLink>
        </p>
      </UiCardFooter>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n, definePageMeta, useSeoMeta } from '#imports';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useAuthClient } from '#composables/useAuthClient';
import { useGoogleOneTap } from '#composables/useGoogleOneTap';
import { usePostHog } from '#composables/usePostHog';
import { useConsentState } from '#bubba/shared/composables/consent/useConsentState';
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('Register', { enabled: false });

definePageMeta({
  middleware: ['guest'],
});

const { $t, $ts, $localeRoute } = useI18n();
useSeoMeta({ title: () => $ts('auth.register.seo.title') });
const { identifyUser, captureEvent } = usePostHog();
const { savePreferences } = useConsentState();

const registerSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, $ts('auth.validation.nameRequired')),
    email: z.string().min(1, $ts('auth.validation.emailRequired')).email($ts('auth.validation.emailInvalid')),
    password: z.string().min(8, $ts('auth.validation.passwordMinLength')),
    confirmPassword: z.string().min(1, $ts('auth.validation.confirmPasswordRequired')),
    agreeTerms: z.literal(true, { error: $ts('auth.register.termsRequired') }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: $ts('auth.validation.passwordsMismatch'),
    path: ['confirmPassword'],
  })
);

const authClient = useAuthClient();

// Trigger Google One Tap prompt
useGoogleOneTap('/account');

const isLoading = ref(false);
const serverError = ref<string | null>(null);
const success = ref(false);

// Consent checkboxes
const agreeTerms = ref(false);
const agreeMarketing = ref(false);
const agreeAnalytics = ref(false);

/** Persist consent choices to cookie after successful registration */
function persistConsentChoices() {
  savePreferences({
    marketing: agreeMarketing.value,
    analytics: agreeAnalytics.value,
    third_party: false,
  });
  log.debug('Persisted registration consent choices', {
    marketing: agreeMarketing.value,
    analytics: agreeAnalytics.value,
  });
}

async function handleRegister(values: Record<string, unknown>) {
  const name = values.name as string;
  const email = values.email as string;
  const password = values.password as string;

  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.signUp.email({
      name,
      email,
      password,
    });

    if (result.error) {
      serverError.value = result.error.message || 'Registration failed. Please try again.';
      return;
    }

    persistConsentChoices();

    if (result.data?.user) {
      identifyUser(result.data.user.id, {
        email: result.data.user.email,
        name: result.data.user.name,
      });
      captureEvent('user_signed_up', { method: 'email' });
    }

    // Consent recording is deferred — at this point the user's Convex session
    // is not yet established (email verification is pending).  Consent
    // preferences are already persisted to cookies via persistConsentChoices()
    // above and will be synced to the backend on first authenticated session.

    success.value = true;
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'An unexpected error occurred.';
  } finally {
    isLoading.value = false;
  }
}

async function handleGoogleSignup() {
  isLoading.value = true;
  serverError.value = null;

  try {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: '/account',
    });
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Google signup failed.';
    isLoading.value = false;
  }
}

async function handleAppleSignup() {
  isLoading.value = true;
  serverError.value = null;

  try {
    await authClient.signIn.social({
      provider: 'apple',
      callbackURL: '/account',
    });
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Apple signup failed.';
    isLoading.value = false;
  }
}
</script>
